import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip as MuiToolTip,
  Typography,
} from "@mui/material";
import { useGetLogsQuery } from "../../Slices/roomNateApis";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useEffect, useRef, useState } from "react";
import { formatDate } from "../../utils/formatDate";
import PreviewIcon from "@mui/icons-material/Preview";
import { Close } from "@mui/icons-material";
import { fabric } from "fabric";
import { findRelevantVertice } from "../../utils/findRelevantVertice";
import { useLocation, useNavigate } from "react-router-dom";
import { withAdminEmbedPrefix } from "../../utils/adminEmbedPaths";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

const LogsTable = () => {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [zonesFilter, setZonesFilter] = useState();
  const [prefrencesFilter, setPrefrencesFilter] = useState();

  const [filterArea, setFilterArea] = useState("");

  const [canvasHeight] = useState(270);
  const [canvasWidth] = useState(270);

  const [statusFilter, setStatusFilter] = useState();

  const [activePage, setActivePage] = useState(1);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const { data, isLoading, refetch } = useGetLogsQuery({
    page_size: 20,
    activePage,
    statusFilter: statusFilter,
    zonesFilter: zonesFilter,
    filterArea: filterArea,
    prefrencesFilter: prefrencesFilter,
    startDate: startDate,
    endDate: endDate,
  });

  const roomData = [
    {
      value: "lounging",
      label: "Living Room",
      img: "/livingRoom.svg",
    },
    {
      value: "dining",
      label: "Dining Room",
      img: "/diningRoom.svg",
    },
    {
      value: "sleeping",
      label: "Bedroom",
      img: "/bedRoom.svg",
    },
    {
      value: "workingvanity",
      label: "Home Office",
      img: "/homeOffice.svg",
    },
    {
      value: "comingandgoing",
      label: "Studio Appartment",
      img: "/images/zones/Cominggoing.svg",
    },
  ];

  const prefrences = [
    {
      value: "watchingtv",
      label: "Watching TV",
      img: "/images/zones/WatchingTV.svg",
    },
    {
      value: "caretaking",
      label: "Caretaking",
      img: "/images/zones/Caretaking.svg",
    },
    {
      value: "storagemedia",
      label: "Living Storing",
      img: "/images/zones/Storingaway.svg",
    },
    {
      value: "gettingready",
      label: "Work Station",
      img: "/images/zones/Gettingready.svg",
    },
    {
      value: "tvstorage",
      label: "TV + Storage",
      img: "/images/zones/Cominggoing.svg",
    },
    {
      value: "bedroomstorage",
      label: "Bedroom Storage",
      img: "/images/zones/Cominggoing.svg",
    },
  ];

  useEffect(() => {
    setActivePage(1);
    refetch();
  }, [
    filterArea,
    zonesFilter,
    statusFilter,
    prefrencesFilter,
    startDate,
    endDate,
    refetch,
  ]);

  useEffect(() => {
    refetch();
  }, [activePage, refetch]);

  const [itemPayload, setItemPayload] = useState({ open: false });
  const handleZonesFilter = (_, value) => {
    setZonesFilter(value);
  };

  const handlePrefrencesFilter = (_, value) => {
    setPrefrencesFilter(value);
  };

  const handleStatusFilter = (_, value) => {
    setStatusFilter(value);
  };

  const handlePageChange = (_, value) => {
    setActivePage(value);
  };

  const statusData = [
    { label: "Generated", value: 1 },
    { label: "Not Generated", value: 2 },
  ];

  const handleOpen = (item) => {
    setItemPayload({ open: true, ...item });
  };

  const handleClose = () => {
    setItemPayload({ open: false });
  };

  const handleAreaChange = (e) => {
    const newValue = Math.max(1, parseInt(e.target.value, 10) || 1);
    setFilterArea(newValue);
  };

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      Date.parse(startDate?.format("YYYY-MM-DD")) >
        Date.parse(endDate?.format("YYYY-MM-DD"))
    ) {
      setEndDate(null); 
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (itemPayload?.open) {
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: canvasWidth,
        height: canvasHeight,
        antiAlias: true,
        selection: false,
      });

      let minX = Number.MAX_VALUE;
      let minY = Number.MAX_VALUE;
      let maxX = Number.MIN_VALUE;
      let maxY = Number.MIN_VALUE;

      itemPayload?.vertices?.forEach((vertex) => {
        vertex.points.forEach((point) => {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        });
      });

      const roomWidth = maxX - minX || 500; 
      const roomHeight = maxY - minY || 500; 

      const scaleX = (canvasWidth - 30) / roomWidth;
      const scaleY = (canvasHeight - 30) / roomHeight;
      const scale = Math.min(scaleX, scaleY);

      const canvasCenterX = canvas.getWidth() / 2;
      const canvasCenterY = canvas.getHeight() / 2;

      canvas.zoomToPoint({ x: canvasCenterX, y: canvasCenterY }, scale);

      const boundingBoxWidth = maxX - minX;
      const boundingBoxHeight = maxY - minY;

      const offsetX = canvasCenterX - (minX + boundingBoxWidth / 2);
      const offsetY = canvasCenterY - (minY + boundingBoxHeight / 2);

      const updatedVertices = itemPayload?.vertices.map((vertex) => {
        const adjustedPoints = vertex.points.map((point) => ({
          x: point.x + offsetX,
          y: point.y + offsetY,
        }));

        return { points: adjustedPoints };
      });

      updatedVertices.forEach((vertex, index) => {
        const points = vertex.points.map(({ x, y }) => ({ x, y }));

        const shape = new fabric.Polygon(points, {
          stroke: "#757575",
          strokeWidth: 10 / scale,
          selectable: false,
        });

        canvas.add(shape);

        for (let i = 0; i < points.length - 1; i++) {
          const startPoint = points[i];
          const endPoint = points[i + 1];

          const lengthInPixels = Math.sqrt(
            Math.pow(endPoint.x - startPoint.x, 2) +
              Math.pow(endPoint.y - startPoint.y, 2)
          );

          const pixelsPerInch = 1;
          const lengthInInches = lengthInPixels / pixelsPerInch;
          const feet = Math.floor(lengthInInches / 12);
          const inches = Math.round(lengthInInches % 12);

          let textX, textY, rotation;

          if (startPoint.y - endPoint.y === 0) {
            textX = (startPoint.x + endPoint.x) / 2;
            textY = endPoint.y;
          }

          if (startPoint.x - endPoint.x === 0) {
            textX = startPoint.x;
            textY = (startPoint.y + endPoint.y) / 2;
          }

          if (
            findRelevantVertice([...updatedVertices], updatedVertices[index], {
              position: {
                x: textX,
                y: textY,
              },
            })[1] === "top"
          ) {
            textX = (startPoint.x + endPoint.x) / 2;
            textY = startPoint.y - 10 / scale;
          } else if (
            findRelevantVertice([...updatedVertices], updatedVertices[index], {
              position: {
                x: textX,
                y: textY,
              },
            })[1] === "bottom"
          ) {
            textX = (startPoint.x + endPoint.x) / 2;
            textY = startPoint.y + 10 / scale;
          } else if (
            findRelevantVertice([...updatedVertices], updatedVertices[index], {
              position: {
                x: textX,
                y: textY,
              },
            })[1] === "left"
          ) {
            textY = (startPoint.y + endPoint.y) / 2;
            textX = startPoint.x - 10 / scale;
            rotation = -90;
          } else if (
            findRelevantVertice([...updatedVertices], updatedVertices[index], {
              position: {
                x: textX,
                y: textY,
              },
            })[1] === "right"
          ) {
            textY = (startPoint.y + endPoint.y) / 2;
            textX = startPoint.x + 10 / scale;
            rotation = -90;
          }

          const lineLengthText = `${feet}' ${inches}"`;
          const text = new fabric.Text(lineLengthText, {
            left: textX,
            top: textY,
            fontSize: 10 / scale,
            fill: "#000000",
            selectable: false,
            angle: rotation,
            originX: "center",
            originY: "center",
          });

          canvas.add(text);
        }

        vertex.points.forEach((point) => {
          const circleRadius = shape.strokeWidth / 2;

          const circle = new fabric.Circle({
            left: point.x - circleRadius - 0.5,
            top: point.y - circleRadius - 0.5,
            radius: circleRadius,
            fill: "#757575",
            selectable: false,
          });

          canvas.add(circle);
        });
      });

      itemPayload?.doors.forEach((door) => {
        const rotation = door?.rotation.z || 0;

        const adjustedX = door.position.x + offsetX;
        const adjustedY = door.position.y + offsetY;

        const x1 = adjustedX - door.width / 2;
        const y1 = adjustedY;
        const x2 = adjustedX + door.width / 2;
        const y2 = adjustedY;

        const doorLine = new fabric.Line([x1, y1, x2, y2], {
          stroke: "red",
          strokeWidth: 2 / scale,
          strokeDashArray: [5, 5], 
          selectable: false,
          originX: "center",
          originY: "center",
          angle: rotation * (180 / Math.PI), 
        });

        const checkDirection = findRelevantVertice(
          [...updatedVertices],
          updatedVertices[door.lineId],
          { position: { x: adjustedX, y: adjustedY } }
        )[1];

        let textAdjustedX;
        let textAdjustedY;
        let rotationText = 0;

        if (checkDirection === "top") {
          textAdjustedX = adjustedX;
          textAdjustedY = adjustedY + 14 / scale;
        } else if (checkDirection === "bottom") {
          textAdjustedX = adjustedX;
          textAdjustedY = adjustedY - 14 / scale;
        } else if (checkDirection === "left") {
          textAdjustedX = adjustedX + 14 / scale;
          textAdjustedY = adjustedY;
          rotationText = -90;
        } else if (checkDirection === "right") {
          textAdjustedX = adjustedX - 14 / scale;
          textAdjustedY = adjustedY;
          rotationText = -90;
        }

        const text = new fabric.Text(`${door.width} in`, {
          left: textAdjustedX,
          top: textAdjustedY,
          originX: "center",
          originY: "center",
          fontSize: 8 / scale,
          selectable: false,
          textAlign: "center",
          angle: rotationText,
          fill: "#d32f2f",
          zIndex: 2,
        });

        canvas.add(doorLine, text);
      });

      itemPayload?.windows.forEach((window) => {
        const rotation = window?.rotation.z || 0;

        fabric.Image.fromURL(window.image, (img) => {
          let scaleX = window.width / img.width / scale;
          let scaleY = window.height / img.height / scale;

          if (rotation) {
            const tempScaleX = scaleX;
            scaleX = scaleY;
            scaleY = tempScaleX;
          }

          if (rotation) {
            scaleX = window.height / img.width / scale;
            scaleY = window.width / img.height / scale;
          }

          const adjustedX = window.position.x + offsetX;
          const adjustedY = window.position.y + offsetY;

          img.set({
            scaleX,
            scaleY,
            left: adjustedX,
            top: adjustedY,
            originX: "center",
            originY: "center",
            selectable: false,
            id: window.id,
            type: window.type,
            zIndex: 1,
          });

          const checkDirection = findRelevantVertice(
            [...updatedVertices],
            updatedVertices[window.lineId],
            {
              position: {
                x: adjustedX,
                y: adjustedY,
              },
            }
          )[1];

          let textAdjustedX;
          let textAdjustedY;
          let rotationText;

          if (checkDirection === "top") {
            textAdjustedX = adjustedX;
            textAdjustedY = adjustedY + 12 / scale;
          } else if (checkDirection === "bottom") {
            textAdjustedX = adjustedX;
            textAdjustedY = adjustedY - 12 / scale;
          } else if (checkDirection === "left") {
            textAdjustedX = adjustedX + 12 / scale;
            textAdjustedY = adjustedY;
            rotationText = -90;
          } else if (checkDirection === "right") {
            textAdjustedX = adjustedX - 12 / scale;
            textAdjustedY = adjustedY;
            rotationText = -90;
          }

          const text = new fabric.Text(`${window.width} in`, {
            left: textAdjustedX,
            top: textAdjustedY, 
            originX: "center",
            originY: "center",
            fontSize: 8 / scale, 
            selectable: false,
            textAlign: "center",
            angle: rotationText,
            fill: "#6661ef", 
            zIndex: 2, 
          });

          canvas.add(img, text);
        });
      });

      itemPayload?.firePlaces?.forEach((firePlace) => {
        const rotation = firePlace?.rotation.z || 0;

        fabric.Image.fromURL(firePlace.image, (img) => {
          const check = findRelevantVertice(
            [...itemPayload?.vertices],
            itemPayload?.vertices?.[firePlace?.lineId],
            firePlace
          );

          let scaleX = firePlace.width / img.width / scale;
          let scaleY = firePlace.height / img.height / scale;

          if (rotation) {
            const tempScaleX = scaleX;
            scaleX = scaleY;
            scaleY = tempScaleX;
          }

          if (rotation) {
            scaleX = firePlace.height / img.width / scale;
            scaleY = firePlace.width / img.height / scale;
          }

          const direction = check[1];

          img.set({
            scaleX: scaleX,
            scaleY: scaleY,
            selectable: false,
            hasControls: false,
            hasBorders: false,
            id: firePlace.id,
            type: firePlace.type,
            originX: "center",
            originY: "center",
          });

          let angleDeg = 0;

          const adjustedX = firePlace.position.x + offsetX;
          const adjustedY = firePlace.position.y + offsetY;

          switch (direction) {
            case "left":
              img.set({
                left: firePlace.position.x + offsetX + 17 / scale,
                top: firePlace.position.y + offsetY,
              });
              break;
            case "right":
              img.set({
                left: firePlace.position.x + offsetX - 17 / scale,
                top: firePlace.position.y + offsetY,
              });
              angleDeg = 180;
              break;
            case "top":
              img.set({
                left: firePlace.position.x + offsetX,
                top: firePlace.position.y + offsetY + 17 / scale,
              });
              break;
            case "bottom":
              img.set({
                left: firePlace.position.x + offsetX,
                top: firePlace.position.y + offsetY - 17 / scale,
              });
              angleDeg = 180;
              break;
            default:
              break;
          }

          img.rotate(angleDeg);

          const checkDirection = findRelevantVertice(
            [...updatedVertices],
            updatedVertices[firePlace.lineId],
            {
              position: {
                x: adjustedX,
                y: adjustedY,
              },
            }
          )[1];

          let textAdjustedX;
          let textAdjustedY;
          let rotationText;

          if (checkDirection === "top") {
            textAdjustedX = adjustedX;
            textAdjustedY = adjustedY + 34 / scale;
          } else if (checkDirection === "bottom") {
            textAdjustedX = adjustedX;
            textAdjustedY = adjustedY - 34 / scale;
          } else if (checkDirection === "left") {
            textAdjustedX = adjustedX + 34 / scale;
            textAdjustedY = adjustedY;
            rotationText = -90;
          } else if (checkDirection === "right") {
            textAdjustedX = adjustedX - 34 / scale;
            textAdjustedY = adjustedY;
            rotationText = -90;
          }

          const text = new fabric.Text(`${firePlace.width} in`, {
            left: textAdjustedX,
            top: textAdjustedY, 
            originX: "center",
            originY: "center",
            fontSize: 8 / scale, 
            selectable: false,
            textAlign: "center",
            angle: rotationText,
            fill: "purple", 
            zIndex: 2, 
          });

          canvas.add(img, text);
        });
      });
    }
  }, [itemPayload, canvasHeight, canvasWidth]);

  return (
    <>
      <TableContainer component={Paper}>
        <Box sx={{ pt: 2, px: 2 }}>
          <Typography variant="h6" component={"h6"} flexGrow={1}>
            Search Filter
          </Typography>

          <Grid container spacing={2} style={{ marginTop: "0px" }}>
            {roomData && (
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  value={zonesFilter}
                  options={roomData}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  fullWidth
                  onChange={handleZonesFilter}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Room" />
                  )}
                />
              </Grid>
            )}

            {prefrences && (
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  value={prefrencesFilter}
                  options={prefrences}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  fullWidth
                  onChange={handlePrefrencesFilter}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Prefrence" />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                value={statusFilter}
                options={statusData}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                fullWidth
                onChange={handleStatusFilter}
                renderInput={(params) => (
                  <TextField {...params} label="Select Status" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Area"
                value={filterArea}
                type="number"
                fullWidth
                inputProps={{ min: 1 }}
                onChange={handleAreaChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer
                  components={["DatePicker"]}
                  sx={{ ".MuiFormControl-root": { width: "100%" } }}
                >
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    slotProps={{
                      actionBar: {
                        actions: ["clear"],
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer
                  components={["DatePicker"]}
                  sx={{ ".MuiFormControl-root": { width: "100%" } }}
                >
                  <DatePicker
                    label="End Date"
                    minDate={startDate}
                    value={endDate}
                    onChange={handleEndDateChange}
                    slotProps={{
                      actionBar: {
                        actions: ["clear"],
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>

            {!isLoading && (
              <Grid item xs={12} mt={2}>
                <Typography variant="h6" mb={2}>
                  Analytics
                </Typography>
                <PieChart
                  generatedLayouts={data?.analytics?.totalGeneratedLayouts}
                  totalUngeneratedLayouts={
                    data?.analytics?.totalUngeneratedLayouts
                  }
                  totalGeneratedLayouts={data?.totalCount}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        <Table sx={{ minWidth: 650, mt: 3 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">Room Type</TableCell>
              <TableCell align="center">Prefrences</TableCell>
              <TableCell align="center">Created At</TableCell>
              <TableCell align="center">Partner</TableCell>
              <TableCell align="center">User</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Visualize</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>Loading...</TableCell>
              </TableRow>
            )}

            {!isLoading && data?.logs?.length > 0 ? (
              data?.logs?.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="center">
                    {row?.request?.zone?.map((item, index) => {
                      const foundRoom = roomData?.find(
                        (item1) => item1?.value == item?.value
                      );

                      return (
                        <div key={index}>
                          {foundRoom ? foundRoom.label : "-"}
                        </div>
                      );
                    })}
                  </TableCell>

                  <TableCell align="center">
                    {row?.request?.zone?.map((item, index) => {
                      const specificRoom = [
                        { value: "lounging" },
                        { value: "dining" },
                      ];

                      const foundSpecificRoom = specificRoom.find(
                        (specificItem) => specificItem.value === item.value
                      );

                      if (foundSpecificRoom) {
                        return (
                          <div key={index}>
                            Capacity: {item.capacity || "-"}
                          </div>
                        );
                      }

                      if (
                        item.value === "comingandgoing" ||
                        item.value === "sleeping"
                      ) {
                        return (
                          <div key={index}>
                            {row?.request?.items?.[0]?.name || "-"}
                          </div>
                        );
                      }

                      const foundRoom = prefrences?.find(
                        (item1) => item1?.value === item?.value
                      );

                      return (
                        <div key={index}>
                          {foundRoom ? `${foundRoom.label}` : "-"}
                        </div>
                      );
                    })}
                  </TableCell>

                  <TableCell component="th" scope="row" align="center">
                    {formatDate(row.createdAt)}
                  </TableCell>

                  <TableCell component="th" scope="row" align="center">
                    {row?.partner?.title || ""}
                  </TableCell>

                  <TableCell component="th" scope="row" align="center">
                    {row?.user?.name || ""}
                  </TableCell>

                  <TableCell component="th" scope="row" align="center">
                    <Chip
                      label={
                        row?.response?.status === 200
                          ? "Generated"
                          : "Not Generated"
                      }
                      color={"primary"}
                      sx={{
                        ".MuiChip-label": {
                          color: "white",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row" align="center">
                    <MuiToolTip title="Preview Sketch">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpen(row?.request)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </MuiToolTip>
                    {row?.response?.status === 200 && (
                      <MuiToolTip title="Preview Layouts">
                        <IconButton
                          color="primary"
                          onClick={() =>
                            navigate(withAdminEmbedPrefix(pathname, `/logs/${row._id}`))
                          }
                        >
                          <PreviewIcon />
                        </IconButton>
                      </MuiToolTip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : !isLoading && data?.logs?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No Record !</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
          {data?.logs?.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5}>
                  <Pagination
                    count={data?.totalPages}
                    color="primary"
                    page={activePage}
                    onChange={handlePageChange}
                    sx={{
                      ".MuiPagination-ul": {
                        justifyContent: "center",
                        width: "100%",
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={true}
        disableScrollLock
        sx={{
          visibility: itemPayload?.open ? "visible" : "hidden",
          ".MuiPaper-root": { width: "calc(100% - 25px)", m: 0 },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">View Sketch</DialogTitle>
        <Box position="absolute" top={10} right={0}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <div
            style={{
              width: canvasWidth,
              height: canvasHeight,
              overflow: "hidden",
              margin: "0px auto",
            }}
          >
            <canvas ref={canvasRef} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LogsTable;

const PieChart = ({
  totalUngeneratedLayouts,
  totalGeneratedLayouts,
  generatedLayouts,
}) => {
  const data = {
    labels: [
      `Total Generated Layouts: ${totalGeneratedLayouts}`,
      `Generated Layouts: ${generatedLayouts}`,
      `Total Ungenerated Layouts: ${totalUngeneratedLayouts}`,
    ],
    datasets: [
      {
        data: [0, generatedLayouts, totalUngeneratedLayouts],
        backgroundColor: ["#6661ef", "#2e7d32", "#d32f2f"],
        hoverBackgroundColor: ["#6661ef", "#2e7d32", "#d32f2f"],
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
  };

  return (
    <div>
      <Pie data={data} options={chartOptions} />
    </div>
  );
};
